import React, { useState } from "react"
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Tooltip,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik"
import * as yup from "yup"
import Select from "react-select"

import { AddMedicineFormData } from "../medicineTypes"
import { GenericFormModal } from "components/Modals/AddGenericModal"
import { FormType } from "components/Modals/formTypes"
import { useListQuery } from "helpers/http-client/hooks/useCrud"
import { API_ENDPOINTS } from "helpers/http-client/httpConstants"

interface Option {
  value: string
  label: string
}

const validationSchema = yup.object({
  shelfLifeMonths: yup
    .number()
    .typeError("Shelf Life must be a number")
    .min(1, "Shelf Life must be at least 1 month"),
})

interface AdditionalDetailsFormProps {
  formData: AddMedicineFormData
  onNext: (values: Partial<AddMedicineFormData>) => void
  onBack: () => void
  onSkip: () => void
  onSkipAll: () => void
  isUrdu: boolean
}

export const AdditionalDetailsForm: React.FC<AdditionalDetailsFormProps> = ({
  formData,
  onNext,
  onBack,
  onSkip,
  onSkipAll,
  isUrdu,
}) => {
  const { t } = useTranslation()

  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({})
  const toggleTooltip = (field: string) =>
    setTooltipOpen(prev => ({ ...prev, [field]: !prev[field] }))

  const {
    data: sideEffectsData,
    isLoading: isLoadingSideEffects,
    isError: isErrorSideEffects,
  } = useListQuery(API_ENDPOINTS.SIDE_EFFECTS)

  console.log(
    "Side Effects Data:",
    sideEffectsData,
    "Loading:",
    isLoadingSideEffects,
    "Error:",
    isErrorSideEffects
  )
  const defaultSideEffects: Option[] = [
    { value: "se1", label: "Nausea" },
    { value: "se2", label: "Headache" },
    { value: "se3", label: "Drowsiness" },
    { value: "se4", label: "Allergic Reaction" },
    { value: "se5", label: "Dry Mouth" },
  ]

  const [sideEffects, setSideEffects] = useState<Option[]>(defaultSideEffects)
  const [sideEffectModalOpen, setSideEffectModalOpen] = useState(false)

  const handleAddSideEffectSuccess = (newEffect: any) => {
    const newOption = {
      value: newEffect.sideEffectId,
      label: newEffect.name,
    }
    setSideEffects(prev => [...prev, newOption])
    setSideEffectModalOpen(false)
  }

  function severityColor(severity: string) {
    switch (severity.toLowerCase()) {
      case "mild":
        return "#28a745" // green
      case "moderate":
        return "#ffc107" // yellow
      case "severe":
        return "#dc3545" // red
      default:
        return "#6c757d" // gray
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          storageConditions: formData.storageConditions || "",
          shelfLifeMonths: formData.shelfLifeMonths || "",
          approvalStatus: formData.approvalStatus || "Pending",
          medicineSideEffects: formData.medicineSideEffects || [],
        }}
        validationSchema={validationSchema}
        onSubmit={values => {
          const parsedValues: Partial<AddMedicineFormData> = {
            ...values,
            shelfLifeMonths: values.shelfLifeMonths
              ? parseInt(values.shelfLifeMonths as any, 10)
              : undefined,
          }
          onNext(parsedValues)
        }}
        enableReinitialize
      >
        {({ errors, touched, isValid, dirty, setFieldValue, values }) => (
          <FormikForm dir={isUrdu ? "rtl" : "ltr"} className="p-3">
            <Row>
              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="storageConditions"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Storage Conditions")}{" "}
                    <span id="storageTooltip" className="text-info">
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.storageConditions}
                    target="storageTooltip"
                    toggle={() => toggleTooltip("storageConditions")}
                  >
                    {t(
                      "Conditions for storing the medicine, e.g., Store below 25°C."
                    )}
                  </Tooltip>
                  <Field
                    name="storageConditions"
                    as={Input}
                    id="storageConditions"
                    placeholder={t("e.g., Store below 25°C")}
                    style={{ textAlign: isUrdu ? "right" : "left" }}
                    className="form-control"
                  />
                </FormGroup>
              </Col>
              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="shelfLifeMonths"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Shelf Life (Months)")}{" "}
                    <span id="shelfLifeTooltip" className="text-info">
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.shelfLifeMonths}
                    target="shelfLifeTooltip"
                    toggle={() => toggleTooltip("shelfLifeMonths")}
                  >
                    {t("Duration the medicine remains effective, in months.")}
                  </Tooltip>
                  <Field
                    name="shelfLifeMonths"
                    as={Input}
                    type="number"
                    placeholder={t("e.g., 24")}
                    invalid={
                      touched.shelfLifeMonths && !!errors.shelfLifeMonths
                    }
                    style={{ textAlign: isUrdu ? "right" : "left" }}
                    className="form-control"
                  />
                  <ErrorMessage
                    name="shelfLifeMonths"
                    component={FormFeedback}
                    className={isUrdu ? "fw-bold" : ""}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="approvalStatus"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Approval Status")}{" "}
                    <span id="approvalTooltip" className="text-info">
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.approvalStatus}
                    target="approvalTooltip"
                    toggle={() => toggleTooltip("approvalStatus")}
                  >
                    {t("Current approval status, e.g., Pending, Active.")}
                  </Tooltip>
                  <Field
                    as="select"
                    name="approvalStatus"
                    id="approvalStatus"
                    className="form-control"
                    style={{ textAlign: isUrdu ? "right" : "left" }}
                  >
                    <option value="Pending">{t("Pending")}</option>
                    <option value="Active">{t("Active")}</option>
                    <option value="Inactive">{t("Inactive")}</option>
                  </Field>
                </FormGroup>
              </Col>

              <Col lg={6}>
                <FormGroup className="mb-3">
                  <Label
                    htmlFor="medicineSideEffects"
                    className={isUrdu ? "fw-bold" : ""}
                  >
                    {t("Side Effects")}{" "}
                    <span id="sideEffectsTooltip" className="text-info">
                      ?
                    </span>
                  </Label>
                  <Tooltip
                    placement="top"
                    isOpen={tooltipOpen.medicineSideEffects}
                    target="sideEffectsTooltip"
                    toggle={() => toggleTooltip("medicineSideEffects")}
                  >
                    {t("Select known side effects of the medicine.")}
                  </Tooltip>

                  <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                      <Select
                        isMulti
                        name="medicineSideEffects"
                        options={
                          isErrorSideEffects
                            ? []
                            : (sideEffectsData || []).map((se: any) => ({
                                value: se.sideEffectId,
                                label: se.name, // still needed for accessibility
                                data: se, // keep full data here for custom label rendering
                              }))
                        }
                        isLoading={isLoadingSideEffects}
                        isDisabled={isErrorSideEffects}
                        classNamePrefix="select"
                        placeholder={
                          isErrorSideEffects
                            ? t("Failed to load side effects")
                            : t("Select side effects")
                        }
                        onChange={(selected: any) => {
                          console.log("Selected side effects:", selected)
                          setFieldValue("sideEffects", selected)
                          setFieldValue(
                            "medicineSideEffects",
                            selected.map((s: Option) => ({
                              sideEffectId: s.value,
                            }))
                          )
                        }}
                        styles={{
                          control: (base: any) => ({
                            ...base,
                            textAlign: isUrdu ? "right" : "left",
                          }),
                          multiValueLabel: base => ({
                            ...base,
                            whiteSpace: "normal", // allow multi line for selected values
                          }),
                        }}
                        isRtl={isUrdu}
                        isClearable
                        formatOptionLabel={({ label, data }) => (
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <div style={{ fontWeight: "600" }}>{label}</div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                fontSize: 12,
                                marginTop: 2,
                              }}
                            >
                              <span
                                style={{
                                  backgroundColor: severityColor(data.severity),
                                  color: "white",
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                  fontWeight: "bold",
                                  textTransform: "capitalize",
                                }}
                              >
                                {data.severity}
                              </span>
                              <span>{data.frequency}</span>
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#666",
                                marginTop: 2,
                              }}
                            >
                              {data.description}
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setSideEffectModalOpen(true)}
                      title={t("Add Side Effect")}
                    >
                      +
                    </Button>
                  </div>

                  {isErrorSideEffects && (
                    <div className="text-danger mt-1">
                      {t("Error loading side effects")}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button
                  color="secondary"
                  onClick={onBack}
                  className={isUrdu ? "fw-bold" : "me-2"}
                >
                  {t("Back")}
                </Button>
                <Button
                  color="warning"
                  onClick={onSkip}
                  className={isUrdu ? "fw-bold" : ""}
                >
                  {t("Skip")}
                </Button>
              </div>
              <div>
                <Button
                  color="secondary"
                  onClick={onSkipAll}
                  className={isUrdu ? "fw-bold" : "me-2"}
                >
                  {t("Skip All")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={!(isValid && dirty)}
                  className={isUrdu ? "fw-bold" : ""}
                >
                  {t("Next")}
                </Button>
              </div>
            </div>
          </FormikForm>
        )}
      </Formik>

      <GenericFormModal
        isOpen={sideEffectModalOpen}
        toggle={() => setSideEffectModalOpen(false)}
        formType={FormType.SIDE_EFFECT}
        onSuccess={handleAddSideEffectSuccess}
      />
    </>
  )
}
